import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import useIsMobile from '../utils/useIsMobile';

const PRIORITY_STYLE = {
  critical: { color: '#b91c1c' }, high: { color: '#c2410c' },
  medium:   { color: '#b45309' }, low: { color: '#166534' },
};
const ITEM_STATUS = ['pending', 'in_progress', 'completed'];
const STATUS_META = {
  pending:     { label: 'Pending',      icon: '○', color: 'var(--text-secondary)' },
  in_progress: { label: 'In progress',  icon: '◐', color: '#d97706' },
  completed:   { label: 'Completed',    icon: '✓', color: '#0B6623' },
};

function fmtToday() {
  return new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TodaysPlan() {
  const isMobile = useIsMobile();
  const [plan, setPlan] = useState(null);
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState('cases');          // cases | task
  const [caseSearch, setCaseSearch] = useState('');
  const [task, setTask] = useState({ title: '', priority: 'medium', notes: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      apiFetch('/daily-plans/today'),
      apiFetch('/daily-plans/my-open-cases'),
    ]);
    setPlan(p); setCases(c);
  }, []);

  useEffect(() => { load().catch(e => setErr(e.message)).finally(() => setLoading(false)); }, [load]);

  const locked = plan?.status === 'submitted';

  async function addCase(t) {
    if (locked) return;
    setBusy(true); setErr('');
    try { await apiFetch('/daily-plans/items', { method: 'POST', body: { ticket_id: t.id } }); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function addTask(e) {
    e.preventDefault();
    if (!task.title.trim()) return;
    setBusy(true); setErr('');
    try {
      await apiFetch('/daily-plans/items', { method: 'POST', body: { title: task.title.trim(), priority: task.priority, notes: task.notes.trim() } });
      setTask({ title: '', priority: 'medium', notes: '' });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function cycleStatus(item) {
    if (locked) return;
    const next = ITEM_STATUS[(ITEM_STATUS.indexOf(item.status) + 1) % ITEM_STATUS.length];
    setBusy(true); setErr('');
    try { await apiFetch(`/daily-plans/items/${item.id}`, { method: 'PATCH', body: { status: next } }); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function saveRemark(item, val) {
    try { await apiFetch(`/daily-plans/items/${item.id}`, { method: 'PATCH', body: { eod_remark: val } }); }
    catch (e) { setErr(e.message); }
  }
  async function removeItem(item) {
    if (locked) return;
    setBusy(true); setErr('');
    try { await apiFetch(`/daily-plans/items/${item.id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function submitDay() {
    setBusy(true); setErr('');
    try { const p = await apiFetch('/daily-plans/submit', { method: 'POST' }); setPlan(p); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function reopenDay() {
    setBusy(true); setErr('');
    try { const p = await apiFetch('/daily-plans/reopen', { method: 'POST' }); setPlan(p); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" /></div>;

  const items = plan?.items || [];
  const done = items.filter(i => i.status === 'completed').length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  const filteredCases = cases.filter(c => {
    if (!caseSearch.trim()) return true;
    const q = caseSearch.toLowerCase();
    return c.title?.toLowerCase().includes(q) || c.ticket_number?.toLowerCase().includes(q) || (c.customer_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-header" style={{ alignItems: 'center' }}>
        <div>
          <div className="page-title">📋 Today's Plan</div>
          <div className="page-sub">{fmtToday()}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 999,
          background: locked ? 'var(--status-resolved-bg)' : 'var(--amber-100)',
          color: locked ? 'var(--status-resolved-text)' : '#92400e' }}>
          {locked ? '✓ Submitted' : 'Draft'}
        </span>
      </div>

      {err && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.3fr', gap: 20, alignItems: 'start' }}>

        {/* ── Picker ─────────────────────────────────────────── */}
        <div className="card" style={{ padding: 16, minWidth: 0, opacity: locked ? 0.55 : 1, pointerEvents: locked ? 'none' : 'auto' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[['cases', 'My open cases'], ['task', 'New task']].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                background: tab === id ? '#0B6623' : 'var(--n-100)', color: tab === id ? '#fff' : 'var(--text-secondary)' }}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'cases' && (
            <>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }}>🔍</span>
                <input className="input" value={caseSearch} onChange={e => setCaseSearch(e.target.value)} placeholder="Search my cases" style={{ paddingLeft: 32, fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 460, overflowY: 'auto' }}>
                {filteredCases.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', padding: '10px 4px' }}>No open cases assigned to you.</p>}
                {filteredCases.map(c => (
                  <div key={c.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, margin: 0, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</p>
                      <p style={{ fontSize: 11, margin: '1px 0 0', color: 'var(--text-secondary)' }}>
                        <span style={{ fontFamily: 'monospace' }}>{c.ticket_number}</span>
                        {' · '}<span style={{ textTransform: 'capitalize', fontWeight: 600, ...(PRIORITY_STYLE[c.priority] || {}) }}>{c.priority}</span>
                        {c.customer_name ? ` · ${c.customer_name}` : ''}
                      </p>
                    </div>
                    {c.on_plan
                      ? <span style={{ fontSize: 12, color: '#0B6623', fontWeight: 700, flexShrink: 0 }}>✓ Added</span>
                      : <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => addCase(c)} style={{ flexShrink: 0 }}>+ Add</button>}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'task' && (
            <form onSubmit={addTask} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="input" value={task.title} onChange={e => setTask(t => ({ ...t, title: e.target.value }))} placeholder="What do you need to do?" />
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <select className="input" value={task.priority} onChange={e => setTask(t => ({ ...t, priority: e.target.value }))}>
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p}</option>)}
                  </select>
                </div>
              </div>
              <textarea className="input" rows={2} value={task.notes} onChange={e => setTask(t => ({ ...t, notes: e.target.value }))} placeholder="Notes (optional)" style={{ resize: 'vertical' }} />
              <button className="btn btn-primary" disabled={busy || !task.title.trim()}>+ Add task</button>
            </form>
          )}
        </div>

        {/* ── Plan ───────────────────────────────────────────── */}
        <div className="card" style={{ padding: 16, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>My plan — {items.length} item{items.length === 1 ? '' : 's'}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{done} / {items.length} done</span>
          </div>
          <div style={{ height: 6, background: 'var(--n-100)', borderRadius: 99, marginBottom: 14 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#0B6623', borderRadius: 99, transition: 'width .3s' }} />
          </div>

          {items.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', padding: '20px 4px', textAlign: 'center' }}>
              Nothing planned yet. Add cases or a task from the left.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => {
              const meta = STATUS_META[item.status];
              return (
                <div key={item.id} style={{ border: `1px solid ${item.status === 'in_progress' ? 'var(--amber-100)' : 'var(--border-subtle)'}`, borderRadius: 8, padding: '9px 11px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <button onClick={() => cycleStatus(item)} disabled={busy || locked} title="Click to change status"
                      style={{ background: 'none', border: 'none', cursor: locked ? 'default' : 'pointer', fontSize: 20, lineHeight: 1, color: meta.color, flexShrink: 0, padding: 0, marginTop: 1 }}>
                      {meta.icon}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13.5, margin: 0, color: 'var(--text-primary)', textDecoration: item.status === 'completed' ? 'line-through' : 'none' }}>{item.title}</p>
                      <p style={{ fontSize: 11, margin: '2px 0 0', color: meta.color, fontWeight: 600 }}>
                        {item.ticket_id ? <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>Case</span> : <span style={{ color: 'var(--text-secondary)' }}>Own task</span>}
                        {' · '}{meta.label}
                        {!item.ticket_id && item.priority ? <span style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}> · {item.priority}</span> : null}
                      </p>
                      {(item.status !== 'completed' || item.eod_remark) && (
                        <input defaultValue={item.eod_remark || ''} disabled={locked}
                          onBlur={e => { if (e.target.value !== (item.eod_remark || '')) saveRemark(item, e.target.value); }}
                          placeholder="EOD remark (why pending / what was done)"
                          className="input" style={{ marginTop: 6, fontSize: 12, padding: '5px 9px' }} />
                      )}
                    </div>
                    {!locked && (
                      <button onClick={() => removeItem(item)} disabled={busy} title="Remove"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 15, flexShrink: 0, padding: 0 }}>✕</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16 }}>
            {!locked ? (
              <button onClick={submitDay} disabled={busy || items.length === 0} className="btn btn-primary"
                style={{ width: '100%', background: '#0B6623', justifyContent: 'center' }}>
                🔒 Close day (EOD)
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12.5, color: 'var(--status-resolved-text)', textAlign: 'center', fontWeight: 600 }}>
                  ✓ Day submitted{plan.submitted_at ? ` at ${new Date(plan.submitted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                </div>
                <button onClick={reopenDay} disabled={busy} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                  ↺ Reopen (today only)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
